/**
 * album-manager/search — trang, ô tìm và bộ lọc của trang Quản lý album nằm
 * TRÊN ĐƯỜNG DẪN (/albums?page=3&q=thu&mine=true).
 *
 * Vì sao: bấm thẻ là mở album ngay trong thẻ trình duyệt đang xem. Người dùng
 * xem xong bấm Quay lại phải về ĐÚNG trang và bộ lọc lúc nãy — giữ trong state
 * của trang thì quay lại là mất sạch.
 */

import type { AlbumStage, AlbumTab } from "./api";

export type AlbumSearch = {
  /** Thẻ đang mở; vắng = "Album của đơn". */
  tab?: Exclude<AlbumTab, "order">;
  /** Chỉ có mặt khi lớn hơn 1. */
  page?: number;
  q?: string;
  branch?: string;
  stage?: AlbumStage;
  tag?: string;
  /** Bộ lọc sản phẩm (CEO chốt 09/10/2026): thương hiệu và/hoặc dòng sản phẩm của đơn. */
  brand?: string;
  line?: string;
  /** Vắng mặt = theo mặc định của người xem (albumManagerMineByDefault). */
  mine?: boolean;
};

/** Bản vá: đặt một khoá về `undefined` là gỡ khoá đó khỏi đường dẫn. */
export type AlbumSearchPatch = { [K in keyof AlbumSearch]?: AlbumSearch[K] | undefined };

/** Mã thương hiệu / dòng sản phẩm — cùng khuôn máy chủ nhận (album_list_handler.go). */
const PRODUCT_CODE = /^[a-z0-9_]{1,40}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MAX_Q = 100;

/** Chữ từ một giá trị trên đường dẫn. Bộ định tuyến đọc `?q=2610` thành SỐ, nên nhận cả số. */
function text(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "";
}

/** Đọc và chuẩn hoá tham số trên đường dẫn. Giá trị lạ bị bỏ, không ném lỗi. */
export function parseAlbumSearch(raw: Record<string, unknown>): AlbumSearch {
  const out: AlbumSearch = {};

  const tab = raw["tab"];
  if (tab === "loose" || tab === "expiring" || tab === "trash" || tab === "retouch_pending") out.tab = tab;

  const rawPage = raw["page"];
  const page = typeof rawPage === "string" ? Number(rawPage) : rawPage;
  if (typeof page === "number" && Number.isInteger(page) && page > 1) out.page = page;

  // Cắt theo KÝ TỰ (máy chủ đếm ký tự): cắt theo đơn vị mã là có thể cắt đôi một ký tự.
  const q = Array.from(text(raw["q"])).slice(0, MAX_Q).join("");
  if (q) out.q = q;

  const branch = text(raw["branch"]);
  if (branch) out.branch = branch;

  const stage = raw["stage"];
  if (stage === "original" || stage === "final") out.stage = stage;

  const tag = text(raw["tag"]);
  if (UUID.test(tag)) out.tag = tag.toLowerCase();

  const brand = text(raw["brand"]);
  if (PRODUCT_CODE.test(brand)) out.brand = brand;
  const line = text(raw["line"]);
  if (PRODUCT_CODE.test(line)) out.line = line;

  const mine = raw["mine"];
  if (mine === true || mine === "true") out.mine = true;
  else if (mine === false || mine === "false") out.mine = false;

  return out;
}

/**
 * Áp một bản vá lên tham số hiện tại.
 *
 * Bản vá không nhắc tới `page` ⇒ về trang 1: đổi thẻ, ô tìm hay bộ lọc là một
 * tập kết quả mới, trang 3 của tập cũ vô nghĩa với nó.
 */
export function patchAlbumSearch(search: AlbumSearch, patch: AlbumSearchPatch): AlbumSearch {
  const merged: Record<string, unknown> = { ...search, ...patch };
  if (!("page" in patch)) delete merged["page"];
  return parseAlbumSearch(merged);
}

/** Số tiêu chí đang bật — con số trên nút "Bộ lọc" ở điện thoại. Ô tìm không tính: nó nằm ngoài hộp lọc. */
export function countActiveFilters(f: {
  branch: string | null;
  stage: AlbumStage | null;
  tag: string | null;
  /** Thương hiệu + dòng là MỘT ô chọn nên tính MỘT tiêu chí. */
  product?: boolean;
  mine: boolean;
}): number {
  return [f.branch, f.stage, f.tag].filter(Boolean).length + (f.product ? 1 : 0) + (f.mine ? 1 : 0);
}
